import { ICellEditorAngularComp } from 'ag-grid-angular';
import { AfterViewInit, Component, OnDestroy, ViewChild, ViewContainerRef, EventEmitter, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import moment, { Moment } from 'moment-timezone';
import { SelectionChange } from '@shared/components/inputs/year-picker/year-picker.component';
import { validateYearInput, YEAR_VALIDATION_CONFIG } from '@shared/components/inputs/year-picker/year-validation.util';

const UTC_EDP_INPUT_FORMAT = 'YYYY';
const UTC_EDP_OUTPUT_FORMAT = 'YYYY[Z]';

@Component({
  selector: 'miq-ag-year',
  templateUrl: './ag-year.component.html',
  styleUrls: ['./ag-year.component.scss'],
  standalone: false,
})
export class AgYearComponent implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  params: any;
  currentValue: any;
  column: any;
  placeholder = 'SELECT YEAR';
  allowPaste = true;
  minDate: string | Moment | null = null;
  maxDate: string | Moment | null = null;

  private focusWhenCreate = false;

  fc: UntypedFormControl;
  @ViewChild('input', { read: ViewContainerRef, static: false }) public input;
  @Output() selectionChange = new EventEmitter<SelectionChange>();

  /**
   * Required by ICellEditorAngularComp
   */
  agInit(params: any) {
    this.params = params;
    this.currentValue = moment(params.value, UTC_EDP_INPUT_FORMAT);
    const paramsObject =
      typeof params?.cellEditorParams === 'function' ? params?.cellEditorParams(params) : params?.cellEditorParams;
    this.column = params.colDef;
    this.focusWhenCreate = !!params.focusWhenCreate || paramsObject?.focusWhenCreate;
  }

  ngOnDestroy() {
    this.input = null;
  }

  /**
   * Required by ICellEditorAngularComp
   */
  ngAfterViewInit() {
    if (this.focusWhenCreate) {
      setTimeout(() => this.input.element.nativeElement.focus());
    }
  }

  /**
   * Required by ICellEditorAngularComp
   */
  getValue() {
    if (this.input && this.input.element) {
      const inputElement = this.input.element.nativeElement.querySelector('input');
      if (inputElement) {
        const typedValue = inputElement.value?.trim() || '';
        const minYear =
          this.minDate !== null && moment.isMoment(this.minDate)
            ? this.minDate.year()
            : YEAR_VALIDATION_CONFIG.MIN_YEAR;
        const maxYear =
          this.maxDate !== null && moment.isMoment(this.maxDate)
            ? this.maxDate.year()
            : YEAR_VALIDATION_CONFIG.MAX_YEAR;
        const validationResult = validateYearInput(typedValue, minYear, maxYear);

        if (validationResult.isValid) {
          this.currentValue = validationResult.momentDate;
        } else {
          this.currentValue = null;
        }
      }
    }
    return this.currentValue ? this.currentValue.format(UTC_EDP_OUTPUT_FORMAT) : this.currentValue;
  }

  onYearSelected(event: SelectionChange) {
    this.currentValue = event.date;
    this.selectionChange.emit(event);
  }

  onInput(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const typedYear = moment(inputElement.value, UTC_EDP_INPUT_FORMAT, true);
    if (typedYear.isValid()) {
      this.currentValue = typedYear;
      this.selectionChange.emit({ id: this.params.node.id, year: typedYear.year(), date: typedYear });
    }
  }

  onPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData.getData('text');
    const pastedYear = moment(pastedText, UTC_EDP_INPUT_FORMAT, true);
    if (pastedYear.isValid()) {
      this.currentValue = pastedYear;
      this.selectionChange.emit({ id: this.params.node.id, year: pastedYear.year(), date: pastedYear });
    }
    event.preventDefault();
  }
}
